// apps/web/src/features/users/components/UserReactivateConfirm.tsx
// Spec §14.4 / plan Task 5.5.2. ConfirmModal wrapping useReactivateUser.mutate.
// Same `visible`-gated lifecycle as UserDeactivateConfirm — legacy ConfirmModal
// calls onClose immediately after onConfirm, so we stay mounted until the
// mutation settles to guarantee the toast callback lands. Per plan §5.5.2:
//   - cancel:                      external onClose fires.
//   - confirm → success:           external onClose fires.
//   - confirm → error:             dialog stays open for retry; onClose NOT called.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactivateUser } from '@magimp/shared-hooks';
import type { User } from '@magimp/shared';
import { ConfirmModal } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export interface UserReactivateConfirmProps {
  user: User;
  onClose: () => void;
}

export function UserReactivateConfirm({ user, onClose }: UserReactivateConfirmProps) {
  const { t } = useTranslation(['users', 'common']);
  const reactivate = useReactivateUser(user.id);
  const show = useToastStore((s) => s.show);
  const [visible, setVisible] = useState(true);
  const confirmingRef = useRef(false);

  // Reconcile once the mutation settles. See UserDeactivateConfirm for
  // the full rationale; the short version: do NOT call `reactivate.reset()`
  // here — its external-store update doesn't batch with React's setState
  // and would cause an intermediate render that falls through to the
  // cancel branch.
  useEffect(() => {
    if (visible) return;
    if (confirmingRef.current) {
      if (reactivate.isPending) return;
      if (reactivate.isError) {
        confirmingRef.current = false;
        setVisible(true);
        return;
      }
      onClose();
      return;
    }
    onClose();
  }, [visible, reactivate.isPending, reactivate.isError, onClose]);

  if (!visible) return null;

  return (
    <ConfirmModal
      title={t('users:modals.reactivateTitle', { name: user.name })}
      text={t('users:modals.reactivateText')}
      confirmLabel={t('common:actions.reactivate')}
      onConfirm={() => {
        confirmingRef.current = true;
        reactivate.mutate(undefined, {
          onSuccess: () => {
            show({ tone: 'success', text: t('users:toasts.reactivated') });
          },
          onError: (err) => {
            show({ tone: 'error', text: errorMessage(err) });
          },
        });
      }}
      onClose={() => setVisible(false)}
    />
  );
}
